import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useUnreadChatsCount } from '../../hooks/useUnreadChatsCount';
import { useAuth } from '@/hooks/useAuth';
import permissionService from '@/services/permissionService';
import { PERMISSIONS } from '@/constants/permissions';
import styles from './ManagementShell.module.css';
import { 
  Paper, 
  Stack, 
  Group, 
  Text, 
  Badge,
  Box,
  NavLink,
  ThemeIcon,
  Alert
} from '@mantine/core';
import {
  IconDashboard,
  IconChartLine,
  IconFileTypePdf,
  IconRobot,
  IconMessages,
  IconUser,
  IconUsers,
  IconBrandWhatsapp,
  IconBrandInstagram,
  IconCloud,
  IconBrandGoogle,
  IconSettings,
  IconChartBar,
  IconHeadset,
  IconLock,
  IconSparkles,
  IconLifebuoy,
  IconInbox,
  IconAddressBook,
  IconPhone,
  IconTags,
  IconTable,
  IconCpu,
  IconPlug,
  IconDatabase,
  IconBinaryTree2,
  IconTarget,
  IconClipboardList,
  type Icon
} from '@tabler/icons-react';

interface SidebarProps {
  visible?: boolean;
}

interface MenuItem {
  textKey: string;
  icon: string;
  path: string;
  descKey?: string;
  permission?: string;
  onlyForRoles?: string[];
}

interface MenuGroup {
  labelKey: string;
  icon: string;
  defaultOpen?: boolean;
  section?: 'admin' | 'google_admin';
  children?: MenuItem[];
  path?: string;
}

interface UserData {
  id: number;
  username: string;
  email: string;
  role: string;
  client_id?: number | null;
}

interface AuthState {
  user: UserData | null;
  loading: boolean;
}

type IconComponent = React.ComponentType<any>;

export default function Sidebar({ visible = true }: SidebarProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { unreadCount } = useUnreadChatsCount();
  const { user } = useAuth() as AuthState;

  const iconMap: Record<string, IconComponent> = {
    'fa-solid fa-gauge': IconDashboard,
    'fa-solid fa-chart-line': IconChartLine,
    'fa-solid fa-file-pdf': IconFileTypePdf,
    'fa-solid fa-robot': IconRobot,
    'fa-solid fa-comments': IconMessages,
    'fa-solid fa-user-tie': IconUser,
    'fa-solid fa-users': IconUsers,
    'fa-brands fa-whatsapp': IconBrandWhatsapp,
    'fa-brands fa-instagram': IconBrandInstagram,
    'fa-solid fa-cloud': IconCloud,
    'fa-brands fa-google': IconBrandGoogle,
    'fa-solid fa-cog': IconSettings,
    'fa-solid fa-chart-bar': IconChartBar,
    'fa-solid fa-headset': IconHeadset,
    'fa-solid fa-lifebuoy': IconLifebuoy,
    'fa-solid fa-inbox': IconInbox,
    'fa-solid fa-address-book': IconAddressBook,
    'fa-solid fa-phone': IconPhone,
    'fa-solid fa-tags': IconTags,
    'fa-solid fa-sparkles': IconSparkles,
    'fa-solid fa-table': IconTable,
    'fa-solid fa-cpu': IconCpu,
    'fa-solid fa-plug': IconPlug,
    'fa-solid fa-database': IconDatabase,
    'fa-solid fa-binary-tree': IconBinaryTree2,
    'fa-solid fa-target': IconTarget,
    'fa-solid fa-clipboard-list': IconClipboardList
  };

  const menuGroups: MenuGroup[] = [
    {
      labelKey: 'sidebar.overview',
      icon: 'fa-solid fa-gauge',
      path: '/dashboard'
    },
    {
      labelKey: 'sidebar.customerEngagement',
      icon: 'fa-solid fa-comments',
      defaultOpen: true,
      children: [
        { textKey: 'sidebar.conversations', icon: 'fa-solid fa-comments', path: '/conversations' },
        { textKey: 'sidebar.agentConversations', icon: 'fa-solid fa-cpu', path: '/agents/conversations', descKey: 'sidebar.agentConversationsDesc' },
        { textKey: 'sidebar.crm', icon: 'fa-solid fa-target', path: '/leads', descKey: 'sidebar.crmDesc' },
        { textKey: 'sidebar.chatMonitor', icon: 'fa-solid fa-inbox', path: '/advanced/chat-monitor', descKey: 'sidebar.chatMonitorDesc' },
        { textKey: 'sidebar.operatorAssistance', icon: 'fa-solid fa-headset', path: '/advanced/handover' },
        { textKey: 'sidebar.handoverReport', icon: 'fa-solid fa-chart-line', path: '/handover-report', descKey: 'sidebar.handoverReportDesc' }
      ]
    },
    {
      labelKey: 'sidebar.aiAssistants',
      icon: 'fa-solid fa-robot',
      defaultOpen: true,
      children: [
        { textKey: 'sidebar.chatbots', icon: 'fa-solid fa-robot', path: '/chatbots', permission: PERMISSIONS.MANAGE_CHATBOTS },
        { textKey: 'sidebar.agents', icon: 'fa-solid fa-cpu', path: '/agents', descKey: 'sidebar.agentsDesc', permission: PERMISSIONS.MANAGE_CHATBOTS }
        // TODO: Re-enable Flow Builder when fully implemented
        // { textKey: 'sidebar.flowBuilder', icon: 'fa-solid fa-binary-tree', path: '/flow-builder', descKey: 'sidebar.flowBuilderDesc', permission: PERMISSIONS.FLOW_BUILDER }
      ]
    },
    {
      labelKey: 'sidebar.automationsIntegrations',
      icon: 'fa-solid fa-sparkles',
      defaultOpen: false,
      children: [
        { textKey: 'sidebar.automations', icon: 'fa-solid fa-sparkles', path: '/automations', descKey: 'sidebar.automationsDesc', permission: PERMISSIONS.MANAGE_AUTOMATIONS },
        { textKey: 'sidebar.integrations', icon: 'fa-solid fa-plug', path: '/integrations', descKey: 'sidebar.integrationsDesc', permission: PERMISSIONS.MANAGE_AUTOMATIONS },
        { textKey: 'sidebar.customFields', icon: 'fa-solid fa-tags', path: '/whatsapp-cloud/custom-fields', descKey: 'sidebar.customFieldsDesc', permission: PERMISSIONS.MANAGE_AUTOMATIONS }
      ]
    },
    {
      labelKey: 'sidebar.channels',
      icon: 'fa-brands fa-whatsapp',
      defaultOpen: false,
      children: [
        { textKey: 'sidebar.whatsapp', icon: 'fa-brands fa-whatsapp', path: '/whatsapp', permission: PERMISSIONS.MANAGE_WHATSAPP },
        { textKey: 'sidebar.whatsappCloud', icon: 'fa-solid fa-cloud', path: '/whatsapp-cloud', permission: PERMISSIONS.MANAGE_WHATSAPP },
        { textKey: 'sidebar.whatsappContacts', icon: 'fa-solid fa-phone', path: '/whatsapp-cloud/contacts', permission: PERMISSIONS.VIEW_WHATSAPP_CONTACTS },
        { textKey: 'sidebar.whatsappTemplates', icon: 'fa-solid fa-table', path: '/whatsapp-cloud/templates', permission: PERMISSIONS.MANAGE_WHATSAPP_TEMPLATES, onlyForRoles: ['chat_manager'] },
        { textKey: 'sidebar.instagram', icon: 'fa-brands fa-instagram', path: '/instagram', descKey: 'sidebar.instagramDesc', permission: PERMISSIONS.MANAGE_INSTAGRAM }
      ]
    },
    {
      labelKey: 'sidebar.settings',
      icon: 'fa-solid fa-cog',
      path: '/settings'
    },
    {
      labelKey: 'sidebar.administration',
      icon: 'fa-solid fa-users',
      section: 'admin',
      defaultOpen: false,
      children: [
        { textKey: 'sidebar.clients', icon: 'fa-solid fa-user-tie', path: '/clients', permission: PERMISSIONS.MANAGE_CLIENTS },
        { textKey: 'sidebar.users', icon: 'fa-solid fa-users', path: '/users', permission: PERMISSIONS.MANAGE_USERS },
        { textKey: 'sidebar.dripAudit', icon: 'fa-solid fa-chart-bar', path: '/drip-audit', permission: PERMISSIONS.MANAGE_CLIENTS },
        { textKey: 'sidebar.auditTrail', icon: 'fa-solid fa-clipboard-list', path: '/audit-trail', permission: PERMISSIONS.VIEW_AUDIT_TRAIL },
        { textKey: 'sidebar.subscriptionPlans', icon: 'fa-solid fa-credit-card', path: '/admin/piani', permission: PERMISSIONS.MANAGE_CLIENTS },
        { textKey: 'sidebar.fattureInCloud', icon: 'fa-solid fa-file-invoice-dollar', path: '/admin/fatturazione', permission: PERMISSIONS.MANAGE_CLIENTS },
        { textKey: 'sidebar.llmUsage', icon: 'fa-solid fa-chart-line', path: '/admin/llm-usage', onlyForRoles: ['admin_globale'] },
        { textKey: 'sidebar.ragObservability', icon: 'fa-solid fa-magnifying-glass-chart', path: '/admin/rag-observability', onlyForRoles: ['admin_globale'] },
        { textKey: 'sidebar.metaGraphQuota', icon: 'fa-solid fa-gauge-high', path: '/admin/meta-graph-quota', onlyForRoles: ['admin_globale'] }
      ]
    },
    {
      labelKey: 'sidebar.integrations',
      icon: 'fa-brands fa-google',
      section: 'google_admin',
      defaultOpen: false,
      children: [
        { textKey: 'sidebar.googleIntegrations', icon: 'fa-brands fa-google', path: '/google-integrations', descKey: 'sidebar.googleIntegrationsDesc' }
      ]
    }
  ];

  // Persist collapse state
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(() => {
    const saved = localStorage.getItem('sidebar-open-groups');
    if (saved) {
      return JSON.parse(saved);
    }
    // Initialize with defaultOpen values
    const initial: Record<string, boolean> = {};
    menuGroups.forEach(group => {
      initial[group.labelKey] = group.defaultOpen ?? false;
    });
    return initial;
  });

  useEffect(() => {
    localStorage.setItem('sidebar-open-groups', JSON.stringify(openGroups));
  }, [openGroups]);

  const toggleGroup = (labelKey: string) => {
    setOpenGroups(prev => ({
      ...prev,
      [labelKey]: !prev[labelKey]
    }));
  };

  const filteredMenuGroups = menuGroups
    .filter((group) => {
      if (group.section === 'admin') {
        return permissionService.hasPermission(user?.role, PERMISSIONS.MANAGE_USERS);
      }
      if (group.section === 'google_admin') {
        return permissionService.hasPermission(user?.role, PERMISSIONS.GOOGLE_INTEGRATIONS);
      }
      return true;
    })
    .map((group) => {
      if (!group.children) return group;
      const filteredChildren = group.children.filter((child) => {
        if (child.onlyForRoles && !child.onlyForRoles.includes(user?.role || '')) return false;
        if (!child.permission) return true;
        return permissionService.hasPermission(user?.role, child.permission);
      });
      return { ...group, children: filteredChildren };
    })
    .filter((group) => group.path || (group.children && group.children.length > 0));

  return (
    <>
      <style>{`
        @keyframes pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }
      `}</style>

      <Box
        className={styles.sidebar}
        style={{
          width: visible ? '240px' : '0px',
          minWidth: visible ? '240px' : '0px',
          maxWidth: visible ? '240px' : '0px',
          flexShrink: 0,
          height: '100%',
          transition: 'width 0.3s ease, min-width 0.3s ease, max-width 0.3s ease',
          overflow: 'hidden',
          borderRight: visible ? '1px solid rgba(255, 255, 255, 0.08)' : 'none',
          position: 'relative'
        }}
      >
        <Paper
          className={styles.sidebarPanel}
          h="100%"
          radius={0}
          withBorder={false}
          style={{
            display: 'flex',
            flexDirection: 'column',
            borderRight: '1px solid rgba(255, 255, 255, 0.08)'
          }}
        >
          {/* Navigation */}
          <Box p="md" style={{ flex: 1, overflowY: 'auto', paddingTop: '16px' }}>
            <Stack gap="xs">
              {filteredMenuGroups.map((group) => {
                const GroupIcon = iconMap[group.icon] || IconSettings;
                const isGoogleGroup = group.section === 'google_admin';

                // Single item without children (e.g., Overview)
                if (!group.children && group.path) {
                  const isActive = location.pathname === group.path;
                  const pathToNavigate = group.path || '';
                  return (
                    <NavLink
                      className={styles.nav}
                      key={group.labelKey}
                      label={
                        <Text size="sm" fw={500} style={{ color: isActive ? '#a5b4fc' : 'rgba(255, 255, 255, 0.85)' }}>
                          {t(group.labelKey)}
                        </Text>
                      }
                      leftSection={<GroupIcon size={18} style={{ color: isActive ? '#a5b4fc' : 'rgba(255, 255, 255, 0.5)' }} />}
                      active={isActive}
                      onClick={() => pathToNavigate && navigate(pathToNavigate)}
                    />
                  );
                }

                // Group with children
                if (group.children) {
                  const hasActiveChild = group.children.some(child => location.pathname === child.path);
                  // Auto-open group if it contains active child, otherwise use saved state
                  const isOpen = hasActiveChild || (openGroups[group.labelKey] ?? false);

                  return (
                    <NavLink
                      className={`${styles.nav} ${styles.navGroup}`}
                      data-opened={hasActiveChild || undefined}
                      key={group.labelKey}
                      label={
                        <Text size="sm" fw={600} style={{ color: hasActiveChild ? '#a5b4fc' : 'rgba(255, 255, 255, 0.9)' }}>
                          {t(group.labelKey)}
                        </Text>
                      }
                      leftSection={<GroupIcon size={18} style={{ color: hasActiveChild ? '#a5b4fc' : 'rgba(255, 255, 255, 0.5)' }} />}
                      onClick={() => toggleGroup(group.labelKey)}
                      opened={isOpen}
                      childrenOffset={28}
                      styles={{
                        chevron: {
                          color: 'rgba(255, 255, 255, 0.5)'
                        }
                      }}
                    >
                      {group.children.map((child) => {
                        const ChildIcon = iconMap[child.icon] || IconSettings;
                        const isActive = location.pathname === child.path;
                        const isChatMonitor = child.path === '/advanced/chat-monitor';
                        const showUnreadBadge = isChatMonitor && unreadCount > 0;

                        return (
                          <NavLink
                            className={styles.nav}
                            key={child.textKey}
                            label={
                              <Group justify="space-between" wrap="nowrap">
                                <Text size="sm" fw={500} style={{ color: isActive ? '#a5b4fc' : 'rgba(255, 255, 255, 0.75)' }}>
                                  {t(child.textKey)}
                                </Text>
                                {showUnreadBadge && (
                                  <Badge 
                                    size="xs" 
                                    variant="filled" 
                                    color="red"
                                    radius="xl"
                                    style={{ 
                                      fontWeight: 700,
                                      minWidth: '20px',
                                      height: '20px',
                                      padding: '0 6px',
                                      animation: unreadCount > 5 ? 'pulse 2s ease-in-out infinite' : 'none'
                                    }}
                                    data-testid="badge-unread-count"
                                  >
                                    {unreadCount > 99 ? '99+' : unreadCount}
                                  </Badge>
                                )}
                                {isGoogleGroup && (
                                  <Badge 
                                    size="xs" 
                                    variant="light" 
                                    color="indigo"
                                    radius="md"
                                    style={{ opacity: 0.9 }}
                                  >
                                    {t('sidebar.badgeNew')}
                                  </Badge>
                                )}
                              </Group>
                            }
                            leftSection={<ChildIcon size={16} style={{ color: isActive ? '#a5b4fc' : 'rgba(255, 255, 255, 0.45)' }} />}
                            active={isActive}
                            onClick={(e) => { 
                              e.stopPropagation(); 
                              navigate(child.path); 
                            }}
                            title={child.descKey ? t(child.descKey) : t(child.textKey)}
                          />
                        );
                      })}
                    </NavLink>
                  );
                }

                return null;
              })}
            </Stack>
          </Box>
        </Paper>
      </Box>
    </>
  );
}
